/*
 * Tolkning av Daily Strength-exporter (com.anthonyng.workoutapp).
 *
 * Passfilen har kolumnerna
 *   Date, Workout name, Exercise, Set, Weight, Reps, Distance, Duration, Measurement unit, Notes
 * där Date är LOKAL tid utan tidszon ("2021-11-15 07:09:00"). Tidigare
 * tolkades den som UTC vilket lade passen 1–2 h fel; nu byggs Date med
 * webbläsarens lokala tidszon.
 *
 * Vikt- och kroppsmåttsfilerna verifieras mot live-data i steg 2. Tills dess
 * känns de igen på sina kolumnnamn (Weight utan Exercise, resp. mått-kolumner)
 * och tolkas bäst möjligt.
 */
import { createBundle, nextLocalId, stableHash, buildExerciseMappings, MEASUREMENT_KINDS } from './importModel.js';

// ── CSV ──────────────────────────────────────────────────────────────────

function detectSep(line) {
  const tabs = (line.match(/\t/g) || []).length;
  const semis = (line.match(/;/g) || []).length;
  const commas = (line.match(/,/g) || []).length;
  if (tabs >= commas && tabs >= semis) return '\t';
  return semis > commas ? ';' : ',';
}

// RFC 4180-fältparser: citerade fält och dubblerade citattecken ("").
function parseFields(line, sep) {
  const fields = [];
  let i = 0;
  while (i <= line.length) {
    if (i === line.length) { fields.push(''); break; }
    if (line[i] === '"') {
      i++;
      let val = '';
      while (i < line.length) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') { val += '"'; i += 2; } else { i++; break; }
        } else val += line[i++];
      }
      fields.push(val.trim());
      if (line[i] === sep) i++;
      else if (i >= line.length) break;
    } else {
      const end = line.indexOf(sep, i);
      if (end === -1) { fields.push(line.slice(i).trim()); break; }
      fields.push(line.slice(i, end).trim());
      i = end + 1;
    }
  }
  return fields;
}

export function parseCsvTable(text) {
  const lines = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').trim().split('\n');
  if (lines.length < 2) throw new Error('Filen innehåller för få rader.');
  const sep = detectSep(lines[0]);
  const headers = parseFields(lines[0], sep).map(h => h.toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const cols = parseFields(lines[i], sep);
    const row = {};
    headers.forEach((h, j) => { row[h] = cols[j] ?? ''; });
    rows.push(row);
  }
  return { headers, rows };
}

// "2021-11-15 07:09:00" (lokal tid) → Date. ISO med Z/offset respekteras.
export function parseLocalDateTime(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  if (/[zZ]$|[+-]\d\d:?\d\d$/.test(s)) { const d = new Date(s); return isNaN(d) ? null : d; }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) { const d = new Date(s); return isNaN(d) ? null : d; }
  return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
}

function parseDurationSeconds(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const parts = s.split(':').map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0];
}

function num(v) {
  if (v == null || v === '') return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return isNaN(n) ? null : n;
}

// ── Igenkänning ──────────────────────────────────────────────────────────

export function classifyCsv(headers) {
  const has = n => headers.includes(n);
  if (has('exercise') && has('date')) return 'workouts';
  const measurementCols = headers.filter(h => /waist|chest|hips|neck|bicep|thigh|calf|forearm|shoulder|midja|bröst|höft|hals|lår|vad/.test(h));
  if (has('date') && measurementCols.length) return 'measurements';
  if (has('date') && (has('weight') || has('body weight') || has('bodyweight') || has('vikt'))) return 'weights';
  if (has('date') && (has('height') || has('längd'))) return 'height';
  return 'unknown';
}

// ── Pass ─────────────────────────────────────────────────────────────────

export function parseWorkoutRows(rows) {
  const sessionsByKey = new Map();
  let skipped = 0;
  for (const r of rows) {
    if (!r.exercise || !r.date) { skipped++; continue; }
    const started = parseLocalDateTime(r.date);
    if (!started) { skipped++; continue; }
    const unit = (r['measurement unit'] || '').toLowerCase();
    const weight = num(r.weight);
    const reps = num(r.reps);
    let distance = num(r.distance);
    if (distance != null) distance = unit === 'km' ? distance * 1000 : unit === 'mi' ? distance * 1609.344 : distance;
    const duration = parseDurationSeconds(r.duration);
    if (weight == null && reps == null && distance == null && duration == null) { skipped++; continue; }

    const name = (r['workout name'] || 'Pass').trim();
    const key = `${started.toISOString()}|${name}`;
    let s = sessionsByKey.get(key);
    if (!s) {
      s = {
        id: nextLocalId('s'),
        externalId: `ds:${stableHash(key)}`,
        name,
        startedAt: started.toISOString(),
        endedAt: null,
        durationSeconds: null,
        notes: null,
        exercises: [],
        setCount: 0,
        status: 'new', statusReason: '', selected: true,
      };
      sessionsByKey.set(key, s);
    }
    let ex = s.exercises.find(e => e.csvName === r.exercise);
    if (!ex) { ex = { csvName: r.exercise, order: s.exercises.length + 1, sets: [] }; s.exercises.push(ex); }
    ex.sets.push({
      order: parseInt(r.set, 10) || ex.sets.length + 1,
      weight: weight != null && unit === 'lb' ? +(weight * 0.45359237).toFixed(2) : weight,
      reps: reps != null ? Math.round(reps) : null,
      distanceMeters: distance,
      durationSeconds: duration,
      notes: r.notes || null,
    });
    s.setCount++;
  }
  const sessions = [...sessionsByKey.values()].sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt));
  // Uppskattad passlängd: 2,5 min per set (min 15, max 150) – används för överlappskontroll.
  for (const s of sessions) s.durationSeconds = Math.min(150, Math.max(15, Math.round(s.setCount * 2.5))) * 60;
  return { sessions, skipped };
}

// ── Vikt & kroppsmått (format bekräftas i steg 2) ────────────────────────

export function parseWeightRows(rows) {
  const items = [];
  for (const r of rows) {
    const date = parseLocalDateTime(r.date);
    const kg = num(r.weight ?? r['body weight'] ?? r.bodyweight ?? r.vikt);
    if (!date || kg == null || kg <= 0) continue;
    items.push({
      id: nextLocalId('w'),
      externalId: `ds:w:${stableHash(date.toISOString())}`,
      date: date.toISOString(),
      weightKg: (r['measurement unit'] || r.unit || '').toLowerCase() === 'lb' ? +(kg * 0.45359237).toFixed(2) : kg,
      fatPercent: num(r['body fat'] ?? r['fat'] ?? r['fat %'] ?? r.fatpercent),
      musclePercent: num(r['muscle'] ?? r['muscle %']),
      waterPercent: num(r['water'] ?? r['water %']),
      status: 'new', statusReason: '', selected: true,
    });
  }
  return items;
}

const MEASUREMENT_HEADER_MAP = [
  ['neckCm', /neck|hals/], ['shouldersCm', /shoulder|skuldr/], ['chestCm', /chest|bröst/],
  ['waistCm', /waist|midja/], ['hipsCm', /hips?|höft/],
  ['bicepsLeftCm', /(left|vänster).*bicep|bicep.*(left|l\b|v\b)/], ['bicepsRightCm', /(right|höger).*bicep|bicep.*(right|r\b|h\b)/],
  ['forearmLeftCm', /(left|vänster).*forearm|forearm.*(left|l\b)/], ['forearmRightCm', /(right|höger).*forearm|forearm.*(right|r\b)/],
  ['thighLeftCm', /(left|vänster).*thigh|thigh.*(left|l\b)|lår.*v/], ['thighRightCm', /(right|höger).*thigh|thigh.*(right|r\b)|lår.*h/],
  ['calfLeftCm', /(left|vänster).*calf|calf.*(left|l\b)|vad.*v/], ['calfRightCm', /(right|höger).*calf|calf.*(right|r\b)|vad.*h/],
];

export function parseMeasurementRows(rows, headers) {
  const colFor = {};
  for (const [key, re] of MEASUREMENT_HEADER_MAP) {
    const h = headers.find(x => re.test(x));
    if (h && !Object.values(colFor).includes(h)) colFor[key] = h;
  }
  const items = [];
  for (const r of rows) {
    const date = parseLocalDateTime(r.date);
    if (!date) continue;
    const values = {};
    let any = false;
    for (const k of MEASUREMENT_KINDS.map(m => m.key)) {
      const v = colFor[k] ? num(r[colFor[k]]) : null;
      if (v != null && v > 0) { values[k] = v; any = true; }
    }
    if (!any) continue;
    items.push({
      id: nextLocalId('m'),
      externalId: `ds:m:${stableHash(date.toISOString())}`,
      date: date.toISOString(),
      values,
      status: 'new', statusReason: '', selected: true,
    });
  }
  return items;
}

// ── Bundle ───────────────────────────────────────────────────────────────

/*
 * files: [{ name, text }] – en eller flera CSV-filer ur samma export.
 * ctx: { exerciseTypes, muscleGroups, aliases }
 */
export function parseDailyStrengthFiles(files, ctx = {}) {
  const bundle = createBundle('daily_strength', files);
  const unknown = [];
  for (const f of files) {
    const { headers, rows } = parseCsvTable(f.text);
    const kind = classifyCsv(headers);
    if (kind === 'workouts') {
      const { sessions, skipped } = parseWorkoutRows(rows);
      bundle.datasets.sessions.items.push(...sessions);
      bundle.datasets.sessions.skippedCount += skipped;
    } else if (kind === 'weights') {
      bundle.datasets.weights.items.push(...parseWeightRows(rows));
    } else if (kind === 'measurements') {
      bundle.datasets.measurements.items.push(...parseMeasurementRows(rows, headers));
    } else if (kind === 'height') {
      for (const r of rows) {
        const date = parseLocalDateTime(r.date); const cm = num(r.height ?? r.längd);
        if (date && cm) bundle.datasets.height.items.push({ id: nextLocalId('h'), date: date.toISOString(), heightCm: cm, status: 'new', statusReason: '', selected: true });
      }
    } else unknown.push(f.name);
  }
  if (unknown.length === files.length) {
    throw new Error(`Kände inte igen kolumnerna i ${unknown.join(', ')}. Väntade Date/Exercise/Weight/Reps (pass) eller Date/Weight (vikt).`);
  }
  bundle.unknownFiles = unknown;
  bundle.datasets.sessions.mappings = buildExerciseMappings(bundle.datasets.sessions.items, ctx);
  return bundle;
}
